// src/app/components/admin/publication-recheck-setting.tsx
//
// Platform section of Global Settings: the scheduled retrieval re-check of
// published content (#133). After a publish, the wind tunnel re-asks the
// web-grounded lanes (ChatGPT Web, AI Mode) the questions the content was
// tested on, once the delay has passed, and writes the result onto the
// publication record. It spends real money on every client, so it has an off
// switch and hard bounds. Backend: app/services/publish_recheck_config.py.

import { useEffect, useState } from "react";
import { Check, Loader2 } from "lucide-react";

import { getRecheckSettings, saveRecheckSettings, type RecheckSettings } from "@/api/content";

const muted = { fontSize: "var(--text-xs)", color: "var(--muted-foreground)", lineHeight: 1.5 } as const;
const field = {
  fontSize: "var(--text-sm)", height: 32, padding: "0 10px", borderRadius: 8, width: 120,
  border: "1px solid var(--border)", backgroundColor: "var(--background)", color: "var(--foreground)",
} as const;

const FIELDS: { key: Exclude<keyof RecheckSettings, "enabled">; label: string; hint: string; unit?: string }[] = [
  { key: "delay_days", label: "Delay after publishing", hint: "Days before a publication's re-check falls due.", unit: "days" },
  { key: "passes", label: "Passes per re-check", hint: "How many times each question is asked on each lane (1–10)." },
  { key: "run_cap_usd", label: "Spend cap per re-check", hint: "A re-check run stops at this spend.", unit: "USD" },
  { key: "monthly_cap_usd", label: "Monthly cap, all clients", hint: "Scheduled re-checks launch only while this month's spend plus running re-checks' caps stays under it.", unit: "USD" },
  { key: "max_per_tick", label: "Re-checks per hour", hint: "At most this many are launched by each hourly tick." },
  { key: "max_attempts", label: "Attempts per publication", hint: "A failed re-check is retried until this many launches, then skipped." },
  { key: "draft_test_cap_usd", label: "Draft test cap for non-admins", hint: "The most a non-admin may set as a draft's wind tunnel spend cap. Admins set any cap.", unit: "USD" },
];

export function PublicationRecheckSetting() {
  const [settings, setSettings] = useState<RecheckSettings | null>(null);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const adopt = (s: RecheckSettings) => {
    setSettings(s);
    setDraft(Object.fromEntries(FIELDS.map((f) => [f.key, String(s[f.key])])));
  };

  useEffect(() => {
    getRecheckSettings().then((r) => adopt(r.settings)).catch((e) => setErr(String(e?.message || e)));
  }, []);

  async function save(values: Partial<RecheckSettings>) {
    setSaving(true);
    setErr("");
    setSaved(false);
    try {
      adopt((await saveRecheckSettings(values)).settings);
      setSaved(true);
    } catch (e: any) {
      setErr(String(e?.message || e));
    } finally {
      setSaving(false);
    }
  }

  const changed = settings != null && FIELDS.some((f) => draft[f.key] !== String(settings[f.key]));

  return (
    <div className="py-6">
      <div className="max-w-2xl">
        <h2 style={{ fontSize: "var(--text-lg)", fontWeight: "var(--font-weight-semibold)", color: "var(--foreground)" }}>
          Publication re-checks
        </h2>
        <p style={{ fontSize: "var(--text-sm)", color: "var(--muted-foreground)", marginTop: 4 }}>
          After content is published, the wind tunnel re-asks the web-grounded lanes (ChatGPT Web, AI Mode) the
          questions it was tested on, and writes what changed onto the publication record. Each re-check is a
          bounded run with its own report. Switched off, nothing launches; due re-checks wait and run once it is
          back on.
        </p>

        {err && (
          <div className="mt-4 rounded-lg px-3 py-2"
            style={{ fontSize: "var(--text-sm)", color: "var(--error-text)", backgroundColor: "var(--error-bg)" }}>
            {err}
          </div>
        )}
        {!settings && !err && (
          <p className="mt-5 inline-flex items-center gap-2" style={muted}>
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
          </p>
        )}

        {settings && (
          <div className="mt-5 flex flex-col gap-4">
            <label className="flex items-center gap-2" style={{ fontSize: "var(--text-sm)", color: "var(--foreground)" }}>
              <input type="checkbox" checked={settings.enabled} disabled={saving}
                onChange={(e) => save({ enabled: e.target.checked })} />
              Scheduled re-checks are {settings.enabled ? "on" : "off"}
            </label>
            {FIELDS.map((f) => (
              <div key={f.key} className="flex flex-wrap items-center gap-3">
                <span style={{ fontSize: "var(--text-sm)", color: "var(--foreground)", minWidth: 220 }}>{f.label}</span>
                <input inputMode="decimal" style={field} value={draft[f.key] ?? ""}
                  onChange={(e) => setDraft((d) => ({ ...d, [f.key]: e.target.value }))} />
                {f.unit && <span style={muted}>{f.unit}</span>}
                <span style={{ ...muted, flexBasis: "100%" }}>{f.hint}</span>
              </div>
            ))}
            <div className="flex items-center gap-3">
              <button type="button" disabled={!changed || saving}
                onClick={() => save(Object.fromEntries(FIELDS.map((f) => [f.key, Number(draft[f.key])])) as Partial<RecheckSettings>)}
                className="rounded-lg border px-3 py-2"
                style={{ fontSize: "var(--text-sm)", cursor: changed && !saving ? "pointer" : "default",
                         borderColor: "var(--border)", opacity: changed ? 1 : 0.6 }}>
                {saving ? "Saving…" : "Save limits"}
              </button>
              {saved && (
                <span className="inline-flex items-center gap-1" style={{ ...muted, color: "var(--alpha-teal)" }}>
                  <Check className="h-3.5 w-3.5" /> Saved
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
